"use client";

import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import React from "react";
import FloatingPanel, { type FloatingPanelTheme } from "../FloatingPanel/FloatingPanel";
import Popover, { type PopoverContentProps, type PopoverProps, type PopoverReferenceProps } from "../Popover/Popover";
import { usePopoverFloating } from "../Popover/PopoverContext";
import { useVisibility } from "../Visibility/Visibility";
import styles from "./Tooltip.module.scss";

export interface TooltipProps extends PopoverProps {
  theme?: FloatingPanelTheme;
  /**
   * Convenience prop for simple tooltips. When provided, the component
   * automatically wraps `children` in a `Tooltip.Trigger` and renders
   * this value inside a `Tooltip.Content` — no manual composition needed.
   *
   * @example
   * // Simple usage — title handles everything
   * <Tooltip title="Save document">
   *   <button>Save</button>
   * </Tooltip>
   *
   * // Advanced usage — compose manually for full control
   * <Tooltip>
   *   <Tooltip.Trigger><button>Save</button></Tooltip.Trigger>
   *   <Tooltip.Content>Save document</Tooltip.Content>
   * </Tooltip>
   */
  title?: React.ReactNode;
  asChild?: boolean;
}

const TooltipThemeContext = React.createContext<FloatingPanelTheme>("dark");

const DefaultTooltip = (props: TooltipProps) => {
  const { asChild, children, visible, theme = "dark", title, enterDelay, interactable, ...otherProps } = props;
  const { visible: contextVisible } = useVisibility();

  // Convenience branch: when `title` is provided, automatically compose
  // Reference + Content so consumers don't need to do it themselves.
  if (title !== undefined) {
    if (title === "") {
      return children;
    }

    return (
      <TooltipThemeContext.Provider value={theme}>
        <Popover
          visible={!contextVisible ? false : visible}
          interactable={interactable}
          enterDelay={enterDelay}
          {...otherProps}
        >
          <TooltipTrigger asChild={asChild}>{children}</TooltipTrigger>
          <TooltipContent>{title}</TooltipContent>
        </Popover>
      </TooltipThemeContext.Provider>
    );
  }

  // Standard composition branch: consumers supply Tooltip.Trigger and
  // Tooltip.Content as children for full control over structure.
  return (
    <TooltipThemeContext.Provider value={theme}>
      <Popover
        visible={!contextVisible ? false : visible}
        interactable={interactable}
        enterDelay={enterDelay}
        {...otherProps}
      >
        {children}
      </Popover>
    </TooltipThemeContext.Provider>
  );
};

const Tooltip = createPluggable<TooltipProps>("deci.tooltip", DefaultTooltip as any);

// Reference
export type TooltipTriggerProps<C extends React.ElementType = "button"> = PopoverReferenceProps<C>;

const DefaultTooltipTrigger = <C extends React.ElementType = "button">(props: TooltipTriggerProps<C>) => {
  return <Popover.Reference {...props} />;
};

const TooltipTrigger = createPluggable<TooltipTriggerProps<any>>("deci.tooltip.trigger", DefaultTooltipTrigger);

// Content
export interface TooltipContentProps extends PopoverContentProps {
  theme?: FloatingPanelTheme;
  placement?: PopoverProps["placement"];
}

const DefaultTooltipContent = (props: TooltipContentProps) => {
  const { className, children, asChild, theme, placement, ...otherProps } = props;
  const themeFromContext = React.useContext(TooltipThemeContext);
  const floating = usePopoverFloating({ portal: false });
  const resolvedTheme = theme ?? themeFromContext;
  const resolvedPlacement = placement ?? floating?.context?.placement;
  const open = floating?.context?.visible;
  return (
    <Popover.Content
      handleVisibility={false}
      asChild
      {...otherProps}
    >
      <FloatingPanel
        asChild={asChild}
        unmountWhenHidden
        data-deci-tooltip-content="true"
        theme={resolvedTheme}
        open={open}
        placement={resolvedPlacement}
        className={clsx(styles.tooltipContent, className)}
      >
        {children}
      </FloatingPanel>
    </Popover.Content>
  );
};

const TooltipContent = createPluggable<TooltipContentProps>("deci.tooltip.content", DefaultTooltipContent);

export default Object.assign(Tooltip, {
  Trigger: TooltipTrigger,
  Content: TooltipContent,
});
